/*
 * This file is part of glowingblue/orell-articles.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Component from 'flarum/common/Component';
import type { ComponentAttrs } from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import ArticleListContent from './ArticleListContent';
import app from 'flarum/forum/app';
import type Mithril from 'mithril';
import type ArticleListState from '../states/ArticleListState';

export interface ArticleListAttrs extends ComponentAttrs {
	state: ArticleListState;
}

/**
 * Modelled on flarum/components/DiscussionList.
 */
export default class ArticleList extends Component<ArticleListAttrs, ArticleListState> {
	oninit(vnode: Mithril.Vnode<ArticleListAttrs, this>) {
		super.oninit(vnode);

		this.state = this.attrs.state;
	}

	view() {
		const { params, loading, articles, nextPage } = this.state;

		let loadMoreElm: Mithril.Children;

		if (loading) {
			loadMoreElm = <LoadingIndicator />;
		} else if (nextPage) {
			loadMoreElm = (
				<Button className="Button Button--primary" onclick={() => this.state.loadMore()}>
					{app.translator.trans('core.forum.discussion_list.load_more_button')}
				</Button>
			);
		}

		// The search query gave no results
		if (articles === false) {
			return (
				<div className="ArticleList">
					{loading ? loadMoreElm : <Placeholder text={app.translator.trans('glowingblue-orell-articles.forum.articles.no_results_text')} />}
				</div>
			);
		}

		// No search was done yet
		if (articles.length === 0 && !loading) {
			return (
				<div className="ArticleList">
					<Placeholder text={app.translator.trans('glowingblue-orell-articles.forum.articles.empty_text')} />
				</div>
			);
		}

		return <ArticleListContent articles={articles} searchQuery={params.q} loadMore={loadMoreElm} />;
	}
}
